import { ApiClient, type ApiRequestOptions } from "@/api/client";
import { getRuntimeConfig } from "@/lib/runtime-config";
import { useAuthStore } from "@/stores/auth";

class AuthAwareClient extends ApiClient {
  private store: ReturnType<typeof useAuthStore>;

  constructor(baseUrl: string, store: ReturnType<typeof useAuthStore>) {
    super(baseUrl);
    this.store = store;
  }

  private async authedHeaders(opts: ApiRequestOptions): Promise<Record<string, string>> {
    const tok = await this.store.ensureFreshToken();
    const headers = { ...(opts.headers ?? {}) };
    if (tok) headers.Authorization = `Bearer ${tok}`;
    return headers;
  }

  private handleStatus(err: unknown): void {
    const status = (err as { status?: number }).status;
    if (status === 401) {
      this.store.signOut();
    }
  }

  override async get<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.get<T>(path, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }

  override async patch<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.patch<T>(path, body, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }

  override async post<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.post<T>(path, body, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }
}

export function useApi() {
  const cfg = getRuntimeConfig();
  const store = useAuthStore();
  const client = new AuthAwareClient(cfg.apiEndpoint, store);
  return { client };
}
